import * as React from 'react';

import * as Yup from 'yup';

import {
	positiveNumberValidator,
	simpleStringValidator,
} from '@common/react/utils/validationHelpers';
import { Nullable } from '@common/typescript/objects/Nullable';

import { stringValidator } from '@app/utilities/validators';
import { DeliveryType, hasMemorialItems, ServiceType } from '@app/objects/Pet';
import { NEW_PET_ID, validationMessage } from '@app/components/Pages/ClinicPetEditor/ClinicApi';
import {
	CrematoryListInfo, getServiceTypePreferenceKind, getServiceTypePreferences, useCrematoryServiceType,
} from '@app/objects/Crematory';
import { allowAdd } from '@app/components/Pages/ClinicPetEditor/ClinicPetsComponents/StoreEntriesModal/StoreEntriesModal';
import { DeliveryManager, useDeliveryManager } from '@app/hooks/Delivery/useDeliveryManager';
import { DeliveryProvider } from '@app/components/Pages/PetEditor/Schemas';
import { ClinicCrematory } from '@app/objects/ClinicCrematory';
import { PetPrice } from '@app/objects/PetPrice';
import { useCrematory } from '@app/hooks/Crematory/useCrematory';

interface ServiceTypeSchema {
	values: Array<ServiceType>;
	names: string;
}

function useServiceTypeSchema(): ServiceTypeSchema {
	const values = new Set();
	const {
		useSemiPrivate,
		useCommunal,
		usePrivate,
		useStorage,
		useProductsOnly,
	} = useCrematoryServiceType();

	if (useCommunal) values.add(ServiceType.Communal);
	if (usePrivate) values.add(ServiceType.Private);
	if (useSemiPrivate) values.add(ServiceType.SemiPrivate);
	if (useStorage) values.add(ServiceType.Storage);
	if (useProductsOnly) values.add(ServiceType.ProductsOnly);

	const types = [...values] as Array<ServiceType>;

	return {
		values: types,
		names: types.map((i: ServiceType): string => ServiceType[i]).join(', '),
	};
}

interface ValidationSchemaConfig {
	crematory?: CrematoryListInfo;
	link?: ClinicCrematory;

	manager: DeliveryManager;
	provider: DeliveryProvider;
}

function isAddressRequired(config: ValidationSchemaConfig, type: Nullable<DeliveryType>, id: Nullable<number | string>): boolean {
	const manager = config.manager;
	const provider = config.provider;
	const value = provider(type, id);

	return manager.isMail(value) || manager.isHomeDropOff(value) || manager.isVetDropOff(value);
}

function isDeliveryClinicRequired(config: ValidationSchemaConfig, type: Nullable<DeliveryType>, id: Nullable<number | string>): boolean {
	const manager = config.manager;
	const provider = config.provider;
	const value = provider(type, id);
	const canSelectDeliveryClinic = config.link?.canSelectDeliveryClinic ?? false;

	return manager.isClinic(value) && canSelectDeliveryClinic;
}

function clearParent(services: Array<PetPrice> | undefined): Array<PetPrice> {
	if (!services) return [];

	return services.map((item: PetPrice) => ({ ...item, parent: null }));
}

export function useValidationSchema(link?: ClinicCrematory) {
	const crematory = useCrematory();
	const manager = useDeliveryManager();
	const provider = React.useCallback((type: Nullable<DeliveryType>, id: Nullable<string | number>) => {
		if (manager.type === 'obsolete') return type;

		return id;
	}, [manager.type]);

	const config: ValidationSchemaConfig = {
		link,
		manager,
		crematory,

		provider,
	};

	const serviceTypeSchema = useServiceTypeSchema();

	return Yup.object().shape({
		ownerLastName: simpleStringValidator,
		name: simpleStringValidator,
		petSpecieId: positiveNumberValidator,
		petBreedId: positiveNumberValidator,
		genderId: Yup.number().moreThan(NEW_PET_ID, validationMessage).required(validationMessage),
		reportedWeight: positiveNumberValidator,
		serviceType: Yup
			.number()
			.oneOf(serviceTypeSchema.values, `Service Type must be one of the following values: ${serviceTypeSchema.names}`)
			.required(validationMessage),
		discountId: Yup.number().positive('Must be positive').nullable(),
		noAshes: Yup.boolean()
			.when('serviceType', {
				is: (serviceType) => ServiceType.Communal === +serviceType,
				then: () => Yup.boolean().oneOf([true], 'Required field'),
			}),
		noSpecialServicesAcknowledged: Yup.boolean().when(['services', 'products', 'urns', 'serviceType', 'noSpecialServicesMasterAcknowledged'], {
			is: (
				services: Array<PetPrice>,
				products: Array<PetPrice>,
				urns: Array<PetPrice>,
				serviceType: ServiceType,
				masterAcknowledged: boolean,
			) => {
				const all = [...services, ...products, ...urns];
				const preferences = getServiceTypePreferences(serviceType, config.crematory);

				return config.crematory?.confirmNoSpecialServices
					&& preferences?.enableSpecialServiceAcknowledgement
					&& !masterAcknowledged
					&& !hasMemorialItems(all);
			},
			then: () => Yup.boolean().oneOf([true], 'Confirm no memorial items were added'),
		}),
		services: Yup
			.array()
			.transform(clearParent)
			.of(
				Yup.object().shape({
					count: Yup.number()
						.required('Quantity is required filed')
						.min(0, 'Quantity should be a positive value')
						.integer('Quantity quantity should be an integer value'),
				}),
			),
		urns: Yup.array().when('serviceType', {
			is: (val) => val !== ServiceType.Communal,
			then: () => Yup
				.array()
				.transform(clearParent)
				.of(
					Yup.object().shape({
						count: Yup.number().when('done', {
							is: (val) => val === false,
							then: () => Yup.number().min(1, 'Count should be a positive value'),
						})
							.required('Count should be a positive value')
							.integer('Count quantity should be an integer value'),
						id: Yup.number().when(['entry', 'nodeId', 'parentId'], {
							is: (entry, nodeId, parentId) => !parentId && !allowAdd(entry, nodeId),
							then: () => Yup.number().max(-3, 'Not all parameters for the urn are selected'),
						}),
					}),
				),
		}),
		deliveryAddress: Yup
			.string()
			.when(['deliveryType', 'deliveryTypeId'], {
				is: (type, id) => isAddressRequired(config, type, id),
				then: () => stringValidator('Delivery address'),
			}),
		deliveryCity: Yup
			.string()
			.when(['deliveryType', 'deliveryTypeId'], {
				is: (type, id) => isAddressRequired(config, type, id),
				then: () => stringValidator('Delivery city'),
			}),
		deliveryStateId: Yup
			.number()
			.nullable()
			.when(['deliveryType', 'deliveryTypeId'], {
				is: (type, id) => isAddressRequired(config, type, id),
				then: () => Yup
					.number()
					.min(1, 'Delivery State is required')
					.required('Delivery State is required'),
			}),
		deliveryClinicId: Yup
			.number()
			.nullable()
			.when(['deliveryType', 'deliveryTypeId'], {
				is: (type, id) => isDeliveryClinicRequired(config, type, id),
				then: () => Yup
					.number()
					.min(1, 'Delivery Clinic is required')
					.required('Delivery Clinic is required'),
			}),
		deliveryZip: Yup
			.string()
			.when(['deliveryType', 'deliveryTypeId'], {
				is: (type, id) => isAddressRequired(config, type, id),
				then: () => stringValidator('Delivery zip'),
			}),
		internalIdNum: Yup.string().when('serviceType', {
			is: (type) => crematory?.[getServiceTypePreferenceKind(type)]?.requireInternalId,
			then: () => stringValidator('Tag Id'),
		}),
	});
}
